import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { getMonitoringSession } from '../services/DeployerControlMonitoringService';
import {
  setActiveSessionError,
  setActiveSessionSuccess,
  upsertSession
} from '../store/slices/deployerControlMonitoringSlice';

interface UseMonitoringSessionPollingOptions {
  aiSystemId: string | undefined;
  pollingInterval?: number;
}

// A monitoring session should complete in well under an hour; a permanently
// stuck session (crashed worker, orphaned queue message) must not poll
// silently forever. 900 polls x 4s = 1 hour before this hook gives up and
// marks itself stalled, mirroring useRepositoryPolling's MAX_POLL_COUNT.
const MAX_POLL_COUNT = 900;
// Tolerate a couple of consecutive failures (a transient network blip)
// before giving up, rather than erroring out on the first missed poll.
const MAX_CONSECUTIVE_ERRORS = 3;

export function useMonitoringSessionPolling({
  aiSystemId,
  pollingInterval = 4000
}: UseMonitoringSessionPollingOptions) {
  const dispatch = useAppDispatch();
  const sessions = useAppSelector(
    (state) => state.deployerControlMonitoring.sessions
  );
  const selectedSessionId = useAppSelector(
    (state) => state.deployerControlMonitoring.selectedSessionId
  );
  const isOffline = useAppSelector((state) => state.appStatus.isOffline);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const consecutiveErrorsRef = useRef(0);
  const [isPolling, setIsPolling] = useState(false);
  const [isStalled, setIsStalled] = useState(false);

  // Guard against a session that belongs to a DIFFERENT AI system than the
  // one this hook is currently tracking — `sessions` is Redux state and can
  // briefly still hold the previous system's data for one render right after
  // aiSystemId changes, before that system's own reset/refetch lands.
  const activeSessionId =
    sessions.find(
      (session) =>
        session.aiSystemId === aiSystemId &&
        (session.status === 'PENDING' || session.status === 'IN_PROGRESS')
    )?.id ?? null;

  const giveUp = useCallback(
    (message: string) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPolling(false);
      setIsStalled(true);
      dispatch(setActiveSessionError(message));
    },
    [dispatch]
  );

  const pollOnce = useCallback(async () => {
    if (!aiSystemId || !activeSessionId || isOffline) return;

    pollCountRef.current += 1;
    if (pollCountRef.current > MAX_POLL_COUNT) {
      giveUp(
        'This analysis is taking much longer than expected. Refresh the page to check its latest status.'
      );
      return;
    }

    try {
      const detail = await getMonitoringSession(aiSystemId, activeSessionId);
      consecutiveErrorsRef.current = 0;
      dispatch(upsertSession(detail));
      if (!selectedSessionId || selectedSessionId === detail.id) {
        dispatch(setActiveSessionSuccess(detail));
      }
    } catch (error) {
      consecutiveErrorsRef.current += 1;
      console.error('Failed to poll monitoring session status:', error);
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        giveUp(
          'Lost connection while checking analysis status. Refresh the page to try again.'
        );
      }
    }
  }, [aiSystemId, activeSessionId, selectedSessionId, isOffline, giveUp]);

  useEffect(() => {
    if (isOffline && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsPolling(false);
      return;
    }
    if (isOffline) return;

    if (activeSessionId && !intervalRef.current) {
      pollCountRef.current = 0;
      consecutiveErrorsRef.current = 0;
      setIsStalled(false);
      setIsPolling(true);
      pollOnce();
      intervalRef.current = setInterval(pollOnce, pollingInterval);
    }

    if (!activeSessionId && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsPolling(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeSessionId, isOffline, pollOnce, pollingInterval]);

  return { isPolling, isStalled };
}
